import store, {CpacStoreState} from "../cpac-store.ts";
import {useSyncExternalStore} from "react";
import {fetchItemList, fetchLandingItemList, fetchProgramItemList, fetchVideoItemList} from "./backend.ts";
import useSWR from "swr";
import {ItemListClass} from "../types/ItemListClass.ts";
import {Language} from "../language/Language.ts";


export const useStore = (selector = (state: CpacStoreState) => state) => useSyncExternalStore(store.subscribe, () => selector(store.getState()))


// Build an ItemListClass from a raw projected list, or log and return undefined when nothing came back.
const toItemList = (raw: any, lang: Language, missingLabel: string): ItemListClass | undefined => {
    if (raw) return ItemListClass.constructInstance(raw, lang)
    console.log(`No item list found ${missingLabel}`)
    return undefined
}

// `componentLocalId` opts a mount into the index-native single-list endpoint (one list loaded
// directly). Every mount now carries one typed id (componentLocalId / programId / videoEpisodeId /
// landingId); a mount with none resolves to no list (the legacy whole-page contentModel.json fallback
// was retired — see the sunset of contentModel.get.groovy).
//
// `refreshIntervalMs` enables SWR polling for this list (used by the always-on live bar). Because
// the SWR cache is shared by key, a poll from one consumer refreshes every consumer of the same
// list — so a home-page section sharing the key stays live without its own timer.
//
// `programId` opts a mount into the program episode-list endpoint (item-list-program.json?id) — the
// per-request, program-filtered list of the program page. The side carousel and the featured hero
// both pass the same programId, so they collapse onto ONE deduped fetch and the hero shows items[0].
//
// `videoEpisodeId` opts a mount into the video (episode) page list endpoint (item-list-video.json?id)
// — the per-request "latest videos" list whose live-vs-VOD shape the endpoint derives from the episode.
// `_listName` / `_storeUrl` / `_id` are vestigial: they fed the retired whole-page contentModel path.
// They stay in the signature (callers pass them positionally) but are no longer read; a follow-up can
// drop them and simplify the call sites.
export const useItemList = (_listName: string, _storeUrl: string, _id: string | undefined,
                            generation?: number, componentLocalId?: string, removeCpacTv?: boolean,
                            refreshIntervalMs?: number, programId?: string, videoEpisodeId?: string,
                            landingId?: string) => {
    const lang = useStore().lang

    // SWR key. For the index-native branch, key ONLY on what the item-list.json response actually
    // depends on (componentLocalId + removeCpacTv + lang + generation) — NOT the page storeUrl/
    // listName/id (fetchItemList ignores them). This lets two different mounts of the same list
    // (e.g. the live bar and the home-livestreams section) collapse onto ONE deduped fetch.
    // `generation` is bumped by the watchdog to force a fresh fetch on recovery; equal on the happy
    // path, so dedup holds. A mount with no typed id has a null key ⇒ SWR does not fetch (no list).
    const swrKey = landingId
        ? {kind: 'landingItemList', landingId, lang, generation}
        : videoEpisodeId
        ? {kind: 'videoItemList', videoEpisodeId, lang, generation}
        : programId
        ? {kind: 'programItemList', programId, lang, generation}
        : componentLocalId
        ? {kind: 'itemList', componentLocalId, removeCpacTv: removeCpacTv ?? false, lang, generation}
        : null
    // Fetch the raw list for this mount — dispatching on which kind of mount it is, mirroring the
    // swrKey branches above — and normalize it to an ItemListClass.
    const fetcher = (): Promise<ItemListClass | undefined> => {
        if (landingId) {
            return fetchLandingItemList(landingId)
                .then(raw => toItemList(raw, lang, `for landing id ${landingId}`))
        }
        if (videoEpisodeId) {
            return fetchVideoItemList(videoEpisodeId)
                .then(raw => toItemList(raw, lang, `for video id ${videoEpisodeId}`))
        }
        if (programId) {
            return fetchProgramItemList(programId)
                .then(raw => toItemList(raw, lang, `for program id ${programId}`))
        }
        if (componentLocalId) {
            return fetchItemList(componentLocalId, removeCpacTv ?? false)
            .then(raw => toItemList(raw, lang, `at ${componentLocalId}`))
        }
        // No typed id ⇒ no list (the swrKey is null, so this is never actually invoked).
        return Promise.resolve(undefined)
    }

    const {data, error, isLoading: loading} = useSWR(
        swrKey,
        fetcher,
        refreshIntervalMs ? {refreshInterval: refreshIntervalMs} : undefined)

    return {loading, list: data, error}
}